import { useEffect, useState } from "react";
import { FloatingLabel, Form, Spinner } from "react-bootstrap";
import { useFormik } from "formik";
import {
  showToastError,
  showToastSuccess,
} from "components/toastify/ToastNotifications";
import { AiOutlineClose } from "react-icons/ai";
import {
  useEditConversionRateMutation,
  useGetSupportedCurrenciesQuery,
} from "api/ruleEngineApi";
import { editConversionRateSchema } from "validation-schema/rule-engine-schema";

interface ConversionRateFormValues {
  fromCurrency: string;
  toCurrency: string;
  exchangeRate: string;
  effectiveDate: string;
  expiryDate: string;
  source: string;
}

type Props = {
  activeModalTab: string;
  onClose?: () => void;
  onSuccess?: () => void;
  selectedRate?: any;
};

const EditConversionRateModal = ({
  activeModalTab,
  onClose,
  onSuccess,
  selectedRate,
}: Props) => {
  const [editConversionRate, { isLoading: isEditing }] =
    useEditConversionRateMutation();

  const [supportedCurrencies, setSupportedCurrencies] = useState<string[]>([]);

  

  const {
    data: supportedCurrenciesData,
    error: currenciesError,
    isFetching: isFetchingCurrencies,
  } = useGetSupportedCurrenciesQuery();

  // Helper function to format date for input
  const formatDateForInput = (date: string | Date | undefined): string => {
    if (!date) return "";
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  };

  // Helper function to get tomorrow's date as default expiry
  const getTomorrowDate = (): string => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };

  const formik = useFormik<ConversionRateFormValues>({
    initialValues: {
      fromCurrency: selectedRate?.fromCurrency || "",
      toCurrency: selectedRate?.toCurrency || "",
      exchangeRate: selectedRate?.exchangeRate?.toString() || "1.0",
      effectiveDate:
        formatDateForInput(selectedRate?.effectiveDate) ||
        new Date().toISOString().split("T")[0],
      expiryDate:
        formatDateForInput(selectedRate?.expiryDate) || getTomorrowDate(),
      source: selectedRate?.source || "",
    },
    validationSchema: editConversionRateSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      
      

      try {
        if (!selectedRate?.id) {
          showToastError("No rule selected for editing");
          return;
        }

        const updateData = {
          rateId: selectedRate.id,
          exchangeRate: parseFloat(values.exchangeRate),
          effectiveDate: new Date(values.effectiveDate).toISOString(),
          expiryDate: new Date(values.expiryDate).toISOString(),
          source: values.source,
        };

        

        const result = await editConversionRate(updateData).unwrap();

        

        showToastSuccess("Currency rule updated successfully!");

        // Call onSuccess to refresh the table data
        if (onSuccess) {
          onSuccess();
        } else if (onClose) {
          onClose();
        }
      } catch (error: any) {
        console.error("Error updating currency rule:", error);
        showToastError(
          error?.data?.ResponseMessage ||
            error?.data?.message ||
            "An error occurred while updating the currency rule"
        );
      }
    },
  });

  const incrementValue = (fieldName: string) => {
    const currentValue =
      parseFloat(
        formik.values[fieldName as keyof typeof formik.values] as string
      ) || 0;
    formik.setFieldValue(fieldName, (currentValue + 0.01).toFixed(2));
  };

  const decrementValue = (fieldName: string) => {
    const currentValue =
      parseFloat(
        formik.values[fieldName as keyof typeof formik.values] as string
      ) || 0;
    formik.setFieldValue(
      fieldName,
      Math.max(0, currentValue - 0.01).toFixed(2)
    );
  };

  // Add debug logs
  useEffect(() => {
    
    
  }, [formik.values, selectedRate]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    
    
    

    formik.handleSubmit(e);
  };

  useEffect(() => {
    if (supportedCurrenciesData) {
      const response = supportedCurrenciesData;
      if (response.data?.items) {
        setSupportedCurrencies(response.data.items);
      }
    }
    if (currenciesError) {
      console.error("Error fetching supported currencies:", currenciesError);
      showToastError("Failed to load supported currencies");
    }
  }, [supportedCurrenciesData, currenciesError]);

  return (
    <div>
      <div
        className="modal-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "45px 24px 45px 24px",
          borderBottom: "none",
        }}
      >
        <h5
          style={{
            margin: 0,
            fontSize: "18px",
            fontWeight: "600",
            color: "#1f2937",
          }}
        >
          Edit Conversion Rate
        </h5>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            fontSize: "20px",
            cursor: "pointer",
            color: "#6b7280",
          }}
        >
          <AiOutlineClose />
        </button>
      </div>

      {/* Form Content */}
      <div style={{ padding: "0 24px 24px 24px" }}>
        <Form onSubmit={handleSubmit}>
          {/* Row 1 - Currency fields (disabled) */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingFromCurrency"
                label="From Currency"
                className="float-input"
              >
                <Form.Select
                  name="fromCurrency"
                  value={formik.values.fromCurrency}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={true}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                    backgroundColor: "#f9fafb",
                    color: "#6b7280",
                    cursor: "not-allowed",
                  }}
                  aria-label="Floating label select example"
                  className="border-0 w-100"
                >
                  <option value="">Select Currency</option>
                  {supportedCurrencies?.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </div>

            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingToCurrency"
                label="To Currency"
                className="float-input"
              >
                <Form.Select
                  name="toCurrency"
                  value={formik.values.toCurrency}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={true}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                    backgroundColor: "#f9fafb",
                    color: "#6b7280",
                    cursor: "not-allowed",
                  }}
                  aria-label="Floating label select example"
                  className="border-0 w-100"
                >
                  <option value="">Select Currency</option>
                  {supportedCurrencies?.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
            </div>
          </div>

          {/* Row 2 - Exchange Rate and Effective Date (editable) */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ flex: 1, maxWidth: "246px", position: "relative" }}>
              <FloatingLabel
                controlId="floatingExchangeRate"
                label="Exchange Rate"
                className="float-input"
              >
                <Form.Control
                  type="number"
                  step="0.01"
                  name="exchangeRate"
                  value={formik.values.exchangeRate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    border: "1px solid #d1d5db",
                    height: "58px",
                    paddingRight: "40px",
                    maxWidth: "246px",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => incrementValue("exchangeRate")}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "10px",
                      cursor: "pointer",
                      color: "#6b7280",
                      padding: "2px 4px",
                      lineHeight: "1",
                    }}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => decrementValue("exchangeRate")}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "10px",
                      cursor: "pointer",
                      color: "#6b7280",
                      padding: "2px 4px",
                      lineHeight: "1",
                    }}
                  >
                    ▼
                  </button>
                </div>
              </FloatingLabel>
              {formik.touched.exchangeRate && formik.errors.exchangeRate && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {typeof formik.errors.exchangeRate === "string"
                    ? formik.errors.exchangeRate
                    : "Invalid exchange rate"}
                </span>
              )}
            </div>

            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingEffectiveDate"
                label="Effective Date"
                className="float-input"
              >
                <Form.Control
                  type="date"
                  name="effectiveDate"
                  value={formik.values.effectiveDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                  }}
                  aria-label="Floating label select example"
                  className="border-0 w-100"
                />
              </FloatingLabel>
              {formik.touched.effectiveDate && formik.errors.effectiveDate && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {typeof formik.errors.effectiveDate === "string"
                    ? formik.errors.effectiveDate
                    : "Invalid effective date"}
                </span>
              )}
            </div>
          </div>

          {/* Row 3 - Expiry Date and Source (editable) */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingExpiryDate"
                label="Expiry Date"
                className="float-input"
              >
                <Form.Control
                  type="date"
                  name="expiryDate"
                  value={formik.values.expiryDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                  }}
                  className="border-0 w-100"
                />
              </FloatingLabel>
              {formik.touched.expiryDate && formik.errors.expiryDate && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {typeof formik.errors.expiryDate === "string"
                    ? formik.errors.expiryDate
                    : "Invalid expiry date"}
                </span>
              )}
            </div>

            {/* <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingSource"
                label="Source"
                className="float-input"
              >
                <Form.Control
                  type="text"
                  name="source"
                  value={formik.values.source}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter source"
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                  }}
                  className="border-0 w-100"
                />
              </FloatingLabel>
              {formik.touched.source && formik.errors.source && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {typeof formik.errors.source === "string"
                    ? formik.errors.source
                    : "Invalid source"}
                </span>
              )}
            </div> */}
          </div>

          {/* Display current exchange rate */}
          <p
            style={{
              marginTop: "36px",
              marginBottom: "45px",
              fontSize: "14px",
              color: "#6b7280",
            }}
          >
            1 {formik.values.fromCurrency} = {formik.values.exchangeRate}{" "}
            {formik.values.toCurrency}
          </p>

          {/* Save Button */}
          <button
            disabled={isEditing}
            type="submit"
            style={{
              width: "100%",
              backgroundColor: "#0ea5e9",
              color: "white",
              border: "none",
              borderRadius: "24px",
              padding: "14px 20px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: isEditing ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              opacity: isEditing ? 0.7 : 1,
              transition: "all 0.2s ease-in-out",
            }}
            onMouseOver={(e) => {
              if (!isEditing) {
                e.currentTarget.style.backgroundColor = "#0284c7";
              }
            }}
            onMouseOut={(e) => {
              if (!isEditing) {
                e.currentTarget.style.backgroundColor = "#0ea5e9";
              }
            }}
          >
            {isEditing ? (
              <>
                Saving... <Spinner size="sm" />
              </>
            ) : (
              "Save Changes"
            )}
          </button>
        </Form>
      </div>
    </div>
  );
};

export default EditConversionRateModal;

